// src/hooks/useGameClient.js
// Хук для управления игровой сессией через WebSocket (Phoenix Channel).
//
// Итерация 7: инвентарь добавлен в Redux через dispatch(updateInventory(payload)).
// Загрузка — метод loadInventory (WS push + receive).

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Gateway from '../api/gateway';
import { 
  updateGameData, 
  updateEmotions, 
  updateGoals, 
  updateTasks,
  updateIntents,
  updateLocationInfo,
  updateEnvironment,
  updateAvailableActions,
  updateConsciousness,
  updateDetailedTasks,
  resetGameData,
  addChatMessage,
  clearChatMessages,
  // 🔥 Итерация 7: инвентарь
  updateInventory,
  setInventoryLoading,
  setInventoryError,
  resetInventory,
} from '../store/slices/gameSlice';

// ============================================================================
// КОНСТАНТЫ
// ============================================================================
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;
const JOIN_TIMEOUT = 15000;
const PING_INTERVAL = 30000;
const ACTION_TIMEOUT = 15000;
const CACHE_TTL = 60000;

// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================================================

const hasAuthToken = () => {
  if (typeof window === 'undefined') return false;
  try {
    const state = window.store?.getState?.();
    return !!(state?.auth?.isAuthenticated || state?.auth?.token);
  } catch (error) {
    return false;
  }
};

const getSelectedCharacterId = () => {
  try {
    return localStorage.getItem('selected_character_id');
  } catch (error) {
    console.warn('Error getting selected character:', error);
    return null;
  }
};

const clearCharacterData = () => {
  try {
    localStorage.removeItem('selected_character_id');
    localStorage.removeItem('character_data');
    localStorage.removeItem('last_created_character');
    localStorage.removeItem('character_id');
  } catch (error) {
    console.warn('Error clearing character data:', error);
  }
};

const saveSelectedCharacter = (characterId, characterData = null) => {
  try {
    if (characterId) {
      localStorage.setItem('selected_character_id', characterId);
      if (characterData) {
        localStorage.setItem('character_data', JSON.stringify(characterData));
      }
    }
  } catch (error) {
    console.warn('Error saving character:', error);
  }
};

// ============================================================================
// ОСНОВНОЙ ХУК useGameClient
// ============================================================================
export const useGameClient = () => {
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);
  const { isAuthenticated: isAuthFromRedux, user } = authState || {};

  const isAuthenticated = useMemo(() => {
    return isAuthFromRedux || hasAuthToken();
  }, [isAuthFromRedux]);

  // ============================================================================
  // СОСТОЯНИЯ
  // ============================================================================
  const [connection, setConnection] = useState({
    status: 'disconnected',
    characterId: null,
    socket: null,
    channel: null,
    error: null,
    lastActivity: null,
    serverUrl: null,
    isInitializing: false,
  });

  const [characterState, setCharacterState] = useState(null);
  const [events, setEvents] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [availableCharacters, setAvailableCharacters] = useState([]);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [shouldAutoConnect, setShouldAutoConnect] = useState(false);

  // ============================================================================
  // 🔧 СОСТОЯНИЯ ДЛЯ ЛОКАЦИИ И ДЕЙСТВИЙ
  // ============================================================================
  const [locationInfo, setLocationInfo] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [environment, setEnvironment] = useState({});
  const [quests, setQuests] = useState([]);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  
  // 🔧 СОСТОЯНИЯ ДЛЯ ЭМОЦИЙ, ЦЕЛЕЙ И ЗАДАЧ
  const [emotions, setEmotions] = useState({});
  const [goals, setGoals] = useState([]);
  const [tasks, setTasks] = useState([]);

  // 🔧 СОСТОЯНИЕ ДЛЯ НАМЕРЕНИЙ (INTENTS)
  const [intents, setIntents] = useState([]);

  // 🔧 СОСТОЯНИЯ ДЛЯ СОЗНАНИЯ
  const [consciousness, setConsciousness] = useState({
    model_summary: {},
    context_focus: {
      focus: 'idle',
      emotional_driver: 'neutral',
      environment_threat: 'low',
      attention_span: 'medium',
      cognitive_load: 30
    },
    recent_reflections: [],
    crkv_metrics: {
      crk: 50,
      k: 50,
      v: 50,
      overall_confidence: 50
    }
  });
  const [detailedTasks, setDetailedTasks] = useState([]);

  // ============================================================================
  // 🔧 КЭШ ДЛЯ ДАННЫХ (ЧТОБЫ НЕ ПЕРЕЗАГРУЖАТЬ)
  // ============================================================================
  const cacheRef = useRef({
    location: { data: null, timestamp: 0, characterId: null },
    actions: { data: null, timestamp: 0, characterId: null },
    emotions: { data: null, timestamp: 0, characterId: null },
    goals: { data: null, timestamp: 0, characterId: null },
    tasks: { data: null, timestamp: 0, characterId: null },
    consciousness: { data: null, timestamp: 0, characterId: null },
    intents: { data: null, timestamp: 0, characterId: null },
  });

  const isLoadingRef = useRef({
    location: false,
    actions: false,
    emotions: false,
    goals: false,
    tasks: false,
    consciousness: false,
    intents: false,
  });

  const lastLoadedCharacterIdRef = useRef(null);

  // ============================================================================
  // REFS
  // ============================================================================
  const connectionRef = useRef(connection);
  const characterStateRef = useRef(characterState);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const joinPromiseRef = useRef(null);
  
  const isLoadingCharactersRef = useRef(false);
  const hasLoadedCharactersRef = useRef(false);

  const isFetchingDetailedRef = useRef(false);
  const lastDetailedTasksCharRef = useRef(null);

  // ============================================================================
  // ОБНОВЛЕНИЕ REFS
  // ============================================================================
  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  useEffect(() => {
    characterStateRef.current = characterState;
  }, [characterState]);

  // ============================================================================
  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ СОБЫТИЙ
  // ============================================================================
  const addGameEvent = useCallback((type, data, options = {}) => {
    const event = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date().toLocaleTimeString(),
      fullTimestamp: new Date().toISOString(),
      ...options,
    };

    setEvents(prev => {
      const newEvents = [event, ...prev];
      return newEvents.slice(0, 100);
    });

    return event;
  }, []);

  const addRawEvent = useCallback((type, payload) => {
    const event = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      payload,
      timestamp: new Date().toISOString(),
    };

    setRawEvents(prev => {
      const newEvents = [...prev, event];
      return newEvents.slice(-200);
    });

    return event;
  }, []);

  const clearEvents = useCallback(() => setEvents([]), []);
  const clearRawEvents = useCallback(() => setRawEvents([]), []);

  // ============================================================================
  // ФУНКЦИЯ ОЧИСТКИ РЕСУРСОВ
  // ============================================================================
  const cleanup = useCallback((isManualDisconnect = false) => {
    console.log('🧹 useGameClient - Cleaning up resources', { isManualDisconnect });
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (joinPromiseRef.current) {
      joinPromiseRef.current = null;
    }

    const currentConnection = connectionRef.current;
    if (currentConnection.channel) {
      try {
        currentConnection.channel.leave();
      } catch (error) {
        console.warn('Error leaving channel:', error);
      }
    }

    if (currentConnection.socket) {
      try {
        currentConnection.socket.disconnect();
      } catch (error) {
        console.warn('Error disconnecting socket:', error);
      }
    }

    setConnection({
      status: 'disconnected',
      characterId: null,
      socket: null,
      channel: null,
      error: null,
      lastActivity: null,
      serverUrl: null,
      isInitializing: false,
    });

    if (!isManualDisconnect) {
      setCharacterState(null);
      setSelectedCharacter(null);
      setLocationInfo(null);
      setAvailableActions([]);
      setEnvironment({});
      setEmotions({});
      setGoals([]);
      setTasks([]);
      setIntents([]);
      setConsciousness({
        model_summary: {},
        context_focus: {
          focus: 'idle',
          emotional_driver: 'neutral',
          environment_threat: 'low',
          attention_span: 'medium',
          cognitive_load: 30
        },
        recent_reflections: [],
        crkv_metrics: {
          crk: 50,
          k: 50,
          v: 50,
          overall_confidence: 50
        }
      });
      setDetailedTasks([]);
      
      // Сбрасываем кэш
      Object.keys(cacheRef.current).forEach(key => {
        cacheRef.current[key] = { data: null, timestamp: 0, characterId: null };
      });
      lastLoadedCharacterIdRef.current = null;
      lastDetailedTasksCharRef.current = null;
      isFetchingDetailedRef.current = false;
      
      dispatch(resetGameData());
      dispatch(clearChatMessages());
      // 🔥 Итерация 7: сброс инвентаря
      dispatch(resetInventory());
    }

    setReconnectAttempts(0);
    setActionInProgress(false);
    setShouldAutoConnect(false);
  }, [dispatch]);

  // ============================================================================
  // ФУНКЦИЯ ЗАПУСКА ПИНГА
  // ============================================================================
  const startPing = useCallback((channel) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (channel && channel.state === 'joined') {
        channel.push('ping', { timestamp: Date.now() })
          .receive('ok', () => {
            if (isMountedRef.current) {
              setConnection(prev => ({
                ...prev,
                lastActivity: new Date().toISOString(),
              }));
            }
          })
          .receive('error', (error) => {
            console.warn('Ping error:', error);
          });
      } else {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      }
    }, PING_INTERVAL);
  }, []);

  // ============================================================================
  // 🔥 Итерация 7: ЗАГРУЗКА ИНВЕНТАРЯ ЧЕРЕЗ WS (push + receive)
  // ============================================================================
  const loadInventory = useCallback(async (characterId) => {
    if (!characterId) {
      console.warn('⚠️ loadInventory: characterId is required');
      return null;
    }

    const channel = connectionRef.current?.channel;
    if (!channel || channel.state !== 'joined') {
      console.warn('⚠️ loadInventory: channel not ready, state=', channel?.state);
      return null;
    }

    dispatch(setInventoryLoading(true));

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('⏰ loadInventory: timeout');
        dispatch(setInventoryError('Таймаут запроса инвентаря'));
        resolve(null);
      }, 5000);

      const push = channel.push('get_inventory', {});
      if (!push || typeof push.receive !== 'function') {
        clearTimeout(timeout);
        dispatch(setInventoryError('WS push не выполнен'));
        resolve(null);
        return;
      }

      push
        .receive('ok', (payload) => {
          clearTimeout(timeout);
          const inv = payload?.inventory || payload?.state?.inventory;
          if (inv && typeof inv === 'object') {
            dispatch(updateInventory(inv));
            resolve(inv);
          } else {
            dispatch(updateInventory({ items: [] }));
            resolve({ items: [] });
          }
        })
        .receive('error', (err) => {
          clearTimeout(timeout);
          const reason = (err && (err.reason || err.error || err.message)) || 'WS error';
          dispatch(setInventoryError(String(reason)));
          resolve(null);
        })
        .receive('timeout', () => {
          clearTimeout(timeout);
          dispatch(setInventoryError('Таймаут запроса инвентаря'));
          resolve(null);
        });
    });
  }, [dispatch]);

  // ============================================================================
  // ЗАГРУЗКА ОСТАЛЬНЫХ ДАННЫХ (как было)
  // ============================================================================

  const isCacheValid = useCallback((cacheKey, characterId) => {
    const cache = cacheRef.current[cacheKey];
    if (!cache) return false;
    if (cache.characterId !== characterId) return false;
    if (!cache.data) return false;
    if (Date.now() - cache.timestamp > CACHE_TTL) return false;
    return true;
  }, []);

  const getCachedData = useCallback((cacheKey) => {
    return cacheRef.current[cacheKey]?.data || null;
  }, []);

  const setCacheData = useCallback((cacheKey, characterId, data) => {
    cacheRef.current[cacheKey] = {
      data: data,
      timestamp: Date.now(),
      characterId: characterId
    };
  }, []);

  // loadLocationInfo
  const loadLocationInfo = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return null;
    if (!forceRefresh && isCacheValid('location', characterId)) {
      const cached = getCachedData('location');
      if (cached) {
        setLocationInfo(prev => ({ ...(prev || {}), ...cached }));
        return cached;
      }
    }
    if (isLoadingRef.current.location) return null;

    try {
      isLoadingRef.current.location = true;
      const response = await Gateway.api.player.getCharacterPosition(characterId);
      const data = response?.data || response;
      let location = null;
      if (data?.location_info?.id && data.location_info.id !== 'unknown') {
        location = data.location_info;
      } else if (data?.data?.location_info?.id && data.data.location_info.id !== 'unknown') {
        location = data.data.location_info;
      } else if (data?.position?.location_info?.id && data.position.location_info.id !== 'unknown') {
        location = data.position.location_info;
      }
      if (location) {
        setCacheData('location', characterId, location);
        setLocationInfo(prev => ({ ...(prev || {}), ...location }));
        dispatch(updateLocationInfo(location));
        if (location.environment && typeof location.environment === 'object' && Object.keys(location.environment).length > 0) {
          setEnvironment(prev => ({ ...(prev || {}), ...location.environment }));
          dispatch(updateEnvironment(location.environment));
        }
        addGameEvent('system', {
          message: `📍 Вы находитесь в ${location.name || 'локации'}`,
          details: location.description || location.long_description || '',
          type: 'location'
        });
        return location;
      }
      return null;
    } catch (error) {
      console.error('❌ useGameClient - Failed to load location:', error);
      return null;
    } finally {
      isLoadingRef.current.location = false;
    }
  }, [addGameEvent, dispatch, isCacheValid, getCachedData, setCacheData]);

  // loadAvailableActions
  const loadAvailableActions = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return [];
    if (!forceRefresh && isCacheValid('actions', characterId)) {
      const cached = getCachedData('actions');
      if (cached) {
        setAvailableActions(cached);
        dispatch(updateAvailableActions(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.actions) return null;

    try {
      isLoadingRef.current.actions = true;
      setActionsLoading(true);
      const response = await Gateway.api.player.getAvailableActions(characterId);
      const data = response?.data || response;
      let actions = [];
      if (data?.actions && Array.isArray(data.actions)) {
        actions = data.actions;
      } else if (data?.data?.actions && Array.isArray(data.data.actions)) {
        actions = data.data.actions;
      } else if (response?.actions && Array.isArray(response.actions)) {
        actions = response.actions;
      }
      setCacheData('actions', characterId, actions);
      setAvailableActions(actions);
      dispatch(updateAvailableActions(actions));
      return actions;
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load actions:', error);
      const fallbackActions = [
        { id: 'move', label: 'Переместиться', type: 'move', params: { direction: 'forward' } },
        { id: 'rest', label: 'Отдохнуть', type: 'rest', params: {} },
        { id: 'explore', label: 'Исследовать', type: 'explore', params: {} },
      ];
      setAvailableActions(fallbackActions);
      dispatch(updateAvailableActions(fallbackActions));
      return fallbackActions;
    } finally {
      isLoadingRef.current.actions = false;
      setActionsLoading(false);
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData]);

  // loadGoals
  const loadGoals = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return [];
    if (!forceRefresh && isCacheValid('goals', characterId)) {
      const cached = getCachedData('goals');
      if (cached) {
        setGoals(cached);
        dispatch(updateGoals(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.goals) return null;

    try {
      isLoadingRef.current.goals = true;
      const goalsData = await Gateway.api.player.getCharacterGoals(characterId);
      if (goalsData && Array.isArray(goalsData) && goalsData.length > 0) {
        setCacheData('goals', characterId, goalsData);
        setGoals(goalsData);
        dispatch(updateGoals(goalsData));
        return goalsData;
      } else {
        setCacheData('goals', characterId, []);
        setGoals([]);
        dispatch(updateGoals([]));
        return [];
      }
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load goals:', error);
      return goals;
    } finally {
      isLoadingRef.current.goals = false;
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData, goals]);

  // loadTasks
  const loadTasks = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return [];
    if (!forceRefresh && isCacheValid('tasks', characterId)) {
      const cached = getCachedData('tasks');
      if (cached) {
        setTasks(cached);
        dispatch(updateTasks(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.tasks) return null;

    try {
      isLoadingRef.current.tasks = true;
      const tasksData = await Gateway.api.player.getCharacterTasks(characterId);
      if (tasksData && Array.isArray(tasksData) && tasksData.length > 0) {
        setCacheData('tasks', characterId, tasksData);
        setTasks(tasksData);
        dispatch(updateTasks(tasksData));
        return tasksData;
      } else {
        setCacheData('tasks', characterId, []);
        setTasks([]);
        dispatch(updateTasks([]));
        return [];
      }
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load tasks:', error);
      return tasks;
    } finally {
      isLoadingRef.current.tasks = false;
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData, tasks]);

  // loadEmotions
  const loadEmotions = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return {};
    if (!forceRefresh && isCacheValid('emotions', characterId)) {
      const cached = getCachedData('emotions');
      if (cached) {
        setEmotions(prev => ({ ...(prev || {}), ...cached }));
        dispatch(updateEmotions(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.emotions) return null;

    try {
      isLoadingRef.current.emotions = true;
      const emotionsData = await Gateway.api.player.getCharacterEmotions(characterId);
      if (emotionsData && typeof emotionsData === 'object' && Object.keys(emotionsData).length > 0) {
        setCacheData('emotions', characterId, emotionsData);
        setEmotions(prev => ({ ...(prev || {}), ...emotionsData }));
        dispatch(updateEmotions(emotionsData));
        return emotionsData;
      } else {
        setCacheData('emotions', characterId, {});
        setEmotions({});
        dispatch(updateEmotions({}));
        return {};
      }
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load emotions:', error);
      return emotions;
    } finally {
      isLoadingRef.current.emotions = false;
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData, emotions]);

  // loadIntents
  const loadIntents = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return [];
    if (!forceRefresh && isCacheValid('intents', characterId)) {
      const cached = getCachedData('intents');
      if (cached) {
        setIntents(cached);
        dispatch(updateIntents(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.intents) return null;

    try {
      isLoadingRef.current.intents = true;
      const channel = connectionRef.current.channel;
      if (channel && channel.state === 'joined') {
        const wsResult = await new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(null), 3000);
          channel.push('get_intents', {})
            .receive('ok', (payload) => { clearTimeout(timeout); resolve(payload); })
            .receive('error', () => { clearTimeout(timeout); resolve(null); })
            .receive('timeout', () => { clearTimeout(timeout); resolve(null); });
        });
        if (wsResult?.intents && Array.isArray(wsResult.intents)) {
          setCacheData('intents', characterId, wsResult.intents);
          setIntents(wsResult.intents);
          dispatch(updateIntents(wsResult.intents));
          return wsResult.intents;
        }
      }
      return intents;
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load intents:', error);
      return intents;
    } finally {
      isLoadingRef.current.intents = false;
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData, intents]);

  // loadConsciousness
  const loadConsciousness = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return null;
    if (!forceRefresh && isCacheValid('consciousness', characterId)) {
      const cached = getCachedData('consciousness');
      if (cached) {
        setConsciousness(prev => ({ ...(prev || {}), ...cached }));
        dispatch(updateConsciousness(cached));
        return cached;
      }
    }
    if (isLoadingRef.current.consciousness) return null;

    try {
      isLoadingRef.current.consciousness = true;
      const channel = connectionRef.current.channel;
      if (!channel) return null;
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(null), 5000);
        channel.push('get_consciousness_details', {})
          .receive('ok', (payload) => {
            clearTimeout(timeout);
            if (payload.details && typeof payload.details === 'object') {
              setCacheData('consciousness', characterId, payload.details);
              setConsciousness(prev => ({ ...(prev || {}), ...payload.details }));
              dispatch(updateConsciousness(payload.details));
              resolve(payload.details);
            } else {
              resolve(null);
            }
          })
          .receive('error', () => { clearTimeout(timeout); resolve(null); })
          .receive('timeout', () => { clearTimeout(timeout); resolve(null); });
      });
    } catch (error) {
      console.warn('⚠️ useGameClient - Failed to load consciousness:', error);
      return null;
    } finally {
      isLoadingRef.current.consciousness = false;
    }
  }, [dispatch, isCacheValid, getCachedData, setCacheData]);

  // loadDetailedTasks
  const loadDetailedTasks = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return [];
    if (isFetchingDetailedRef.current && !forceRefresh) return [];
    if (!forceRefresh && lastDetailedTasksCharRef.current === characterId) return [];

    try {
      isFetchingDetailedRef.current = true;
      const channel = connectionRef.current?.channel;
      if (!channel) {
        isFetchingDetailedRef.current = false;
        return [];
      }
      channel.push('update_detailed_tasks', {})
        .receive('ok', () => {})
        .receive('error', () => {
          dispatch(updateDetailedTasks([]));
          isFetchingDetailedRef.current = false;
        })
        .receive('timeout', () => { isFetchingDetailedRef.current = false; });
      lastDetailedTasksCharRef.current = characterId;
      return [];
    } catch (error) {
      dispatch(updateDetailedTasks([]));
      return [];
    } finally {
      setTimeout(() => { isFetchingDetailedRef.current = false; }, 3000);
    }
  }, [dispatch]);

  // loadAllData
  const loadAllData = useCallback(async (characterId, forceRefresh = false) => {
    if (!characterId) return;
    if (!forceRefresh && lastLoadedCharacterIdRef.current === characterId) return;
    console.log('🔄 useGameClient - Loading all data for character:', characterId);
    lastLoadedCharacterIdRef.current = characterId;

    await Promise.all([
      loadLocationInfo(characterId, forceRefresh),
      loadAvailableActions(characterId, forceRefresh),
      loadEmotions(characterId, forceRefresh),
      loadGoals(characterId, forceRefresh),
      loadTasks(characterId, forceRefresh),
      loadIntents(characterId, forceRefresh),
      loadConsciousness(characterId, forceRefresh),
    ]);
  }, [loadLocationInfo, loadAvailableActions, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness]);

  // loadCharacterData
  const loadCharacterData = useCallback(async (characterId) => {
    if (!characterId) return;
    await Promise.all([
      loadLocationInfo(characterId, true),
      loadAvailableActions(characterId, true),
      loadEmotions(characterId, true),
      loadGoals(characterId, true),
      loadTasks(characterId, true),
      loadIntents(characterId, true),
      loadConsciousness(characterId, true),
    ]);
  }, [loadLocationInfo, loadAvailableActions, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness]);

  // sendStimulus
  const sendStimulus = useCallback(async (characterId, stimulusData) => {
    if (!characterId) throw new Error('characterId is required for sending stimulus');
    try {
      const response = await Gateway.api.player.sendStimulus(characterId, stimulusData);
      if (characterId) {
        setTimeout(() => {
          loadEmotions(characterId, true);
          loadGoals(characterId, true);
          loadTasks(characterId, true);
          loadIntents(characterId, true);
          loadConsciousness(characterId, true);
          loadDetailedTasks(characterId, true).catch(() => {});
        }, 1000);
      }
      return response;
    } catch (error) {
      throw error;
    }
  }, [loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness, loadDetailedTasks]);

  // sendDialogue
  const sendDialogue = useCallback(async (characterId, message) => {
    if (!characterId) throw new Error('characterId is required for sending dialogue');
    const channel = connectionRef.current.channel;
    if (!channel || channel.state !== 'joined') throw new Error('Not connected to game');
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Dialogue timeout')), 15000);
      channel.push('send_dialogue', { message })
        .receive('ok', (response) => { clearTimeout(timeout); resolve(response); })
        .receive('error', (err) => { clearTimeout(timeout); reject(new Error(err.reason || 'Dialogue error')); })
        .receive('timeout', () => { clearTimeout(timeout); reject(new Error('Dialogue timeout')); });
    });
  }, []);

  // sendSuggestion
  const sendSuggestion = useCallback(async (characterId, suggestion) => {
    if (!characterId) throw new Error('characterId is required for sending suggestion');
    const channel = connectionRef.current.channel;
    if (!channel || channel.state !== 'joined') throw new Error('Not connected to game');
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Suggestion timeout')), 15000);
      channel.push('send_suggestion', { suggestion })
        .receive('ok', (response) => { clearTimeout(timeout); resolve(response); })
        .receive('error', (err) => { clearTimeout(timeout); reject(new Error(err.reason || 'Suggestion error')); })
        .receive('timeout', () => { clearTimeout(timeout); reject(new Error('Suggestion timeout')); });
    });
  }, []);

  // getThoughtStream
  const getThoughtStream = useCallback(async (characterId) => {
    if (!characterId) throw new Error('characterId is required for getting thought stream');
    const channel = connectionRef.current.channel;
    if (!channel || channel.state !== 'joined') throw new Error('Not connected to game');
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Thought stream timeout')), 10000);
      channel.push('get_thought_stream', {})
        .receive('ok', (response) => { clearTimeout(timeout); resolve(response); })
        .receive('error', (err) => { clearTimeout(timeout); reject(new Error(err.reason || 'Thought stream error')); })
        .receive('timeout', () => { clearTimeout(timeout); reject(new Error('Thought stream timeout')); });
    });
  }, []);

  // refreshAllData
  const refreshAllData = useCallback(async (characterId) => {
    if (!characterId) characterId = connectionRef.current.characterId;
    if (!characterId) return;
    await Promise.all([
      loadLocationInfo(characterId, true),
      loadAvailableActions(characterId, true),
      loadEmotions(characterId, true),
      loadGoals(characterId, true),
      loadTasks(characterId, true),
      loadIntents(characterId, true),
      loadConsciousness(characterId, true),
      loadInventory(characterId),  // 🔥 Итерация 7: инвентарь
    ]);
    loadDetailedTasks(characterId, true).catch(() => {});
  }, [loadLocationInfo, loadAvailableActions, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness, loadInventory, loadDetailedTasks]);

  // ============================================================================
  // ОСНОВНАЯ ФУНКЦИЯ ПОДКЛЮЧЕНИЯ
  // ============================================================================
  const connect = useCallback(async (characterId, options = {}) => {
    if (!characterId) {
      const error = 'Character ID is required';
      if (isMountedRef.current) {
        setConnection(prev => ({ ...prev, status: 'error', error }));
        addGameEvent('system', { message: error, type: 'error' });
      }
      throw new Error(error);
    }

    const currentConnection = connectionRef.current;
    if (currentConnection.status === 'connected' && currentConnection.characterId === characterId) {
      return currentConnection;
    }

    try {
      if (isMountedRef.current) {
        setConnection(prev => ({
          ...prev,
          status: 'connecting',
          characterId,
          error: null,
          isInitializing: true,
        }));
      }

      addGameEvent('system', { message: 'Подключение к игровому миру...', type: 'info' });

      const authResponse = await Gateway.api.character.getGameAuthToken(characterId);
      const responseData = authResponse.data || authResponse;
      const authData = responseData.data || responseData;
      const { token, server_url } = authData;
      
      if (!token || !server_url) {
        throw new Error('Не удалось получить токен аутентификации для игры');
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('game_auth_token', token);
        localStorage.setItem('game_server_url', server_url);
      }

      const playerId = localStorage.getItem('playerId') || Gateway.utils.generatePlayerId();
      localStorage.setItem('playerId', playerId);

      const gameConnection = await Gateway.socket.connectGame(characterId, {
        token,
        server_url,
        playerId,
        timeout: 15000,
        maxRetries: 3,
        retryDelay: 2000,
        useExponentialBackoff: true,
      });

      // ========================================================================
      // ОБРАБОТЧИКИ СОБЫТИЙ КАНАЛА
      // ========================================================================
      if (gameConnection.channel) {
        
        gameConnection.channel.on('detailed_tasks_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.tasks && Array.isArray(payload.tasks)) {
            setDetailedTasks(payload.tasks);
            dispatch(updateDetailedTasks(payload.tasks));
            isFetchingDetailedRef.current = false;
          }
          addRawEvent('detailed_tasks_update', payload);
        });

        gameConnection.channel.on('detailed_tasks_updated', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.tasks && Array.isArray(payload.tasks)) {
            setDetailedTasks(payload.tasks);
            dispatch(updateDetailedTasks(payload.tasks));
            isFetchingDetailedRef.current = false;
          }
          addRawEvent('detailed_tasks_updated', payload);
        });
        
        gameConnection.channel.on('character_ready', (payload) => {
          console.log('✅ useGameClient - Character is READY!', payload);
          if (!isMountedRef.current) return;
          
          setConnection(prev => ({
            ...prev,
            status: 'connected',
            isInitializing: false,
            lastActivity: new Date().toISOString(),
          }));

          if (characterId && gameConnection.channel) {
            setTimeout(() => {
              loadAllData(characterId);
              // 🔥 Итерация 7: инвентарь через WS push + receive
              loadInventory(characterId);
              
              gameConnection.channel.push('get_emotions', {})
                .receive('ok', (payload) => {
                  if (isMountedRef.current && payload.emotions && Object.keys(payload.emotions).length > 0) {
                    setEmotions(prev => ({ ...(prev || {}), ...payload.emotions }));
                    dispatch(updateEmotions(payload.emotions));
                    setCacheData('emotions', characterId, payload.emotions);
                  }
                }).receive('error', () => {});

              gameConnection.channel.push('get_goals', {})
                .receive('ok', (payload) => {
                  if (isMountedRef.current && payload.goals && Array.isArray(payload.goals)) {
                    setGoals(payload.goals);
                    dispatch(updateGoals(payload.goals));
                    setCacheData('goals', characterId, payload.goals);
                  }
                }).receive('error', () => {});

              gameConnection.channel.push('get_tasks', {})
                .receive('ok', (payload) => {
                  if (isMountedRef.current && payload.tasks && Array.isArray(payload.tasks)) {
                    setTasks(payload.tasks);
                    dispatch(updateTasks(payload.tasks));
                    setCacheData('tasks', characterId, payload.tasks);
                  }
                }).receive('error', () => {});

              gameConnection.channel.push('get_intents', {})
                .receive('ok', (payload) => {
                  if (isMountedRef.current && payload.intents && Array.isArray(payload.intents)) {
                    setIntents(payload.intents);
                    dispatch(updateIntents(payload.intents));
                    setCacheData('intents', characterId, payload.intents);
                  }
                }).receive('error', () => {});

              gameConnection.channel.push('get_consciousness_details', {})
                .receive('ok', (payload) => {
                  if (isMountedRef.current && payload.details) {
                    setConsciousness(prev => ({ ...(prev || {}), ...payload.details }));
                    dispatch(updateConsciousness(payload.details));
                    setCacheData('consciousness', characterId, payload.details);
                  }
                }).receive('error', () => {});

              loadDetailedTasks(characterId, true).catch(() => {});
            }, 500);
          }

          addGameEvent('system', { message: 'Персонаж активен и готов к игре', type: 'success' });
        });

        gameConnection.channel.on('consciousness_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.details && typeof payload.details === 'object' && Object.keys(payload.details).length > 0) {
            setConsciousness(prev => ({ ...(prev || {}), ...payload.details }));
            dispatch(updateConsciousness(payload.details));
            setCacheData('consciousness', characterId, payload.details);
          }
          addRawEvent('consciousness_update', payload);
        });

        gameConnection.channel.on('character_error', (payload) => {
          console.error('❌ useGameClient - Character activation failed', payload);
          if (!isMountedRef.current) return;
          setConnection(prev => ({
            ...prev,
            status: 'error',
            isInitializing: false,
            error: payload.reason || 'Activation failed',
          }));
          addGameEvent('system', { message: 'Ошибка активации персонажа', type: 'error', details: payload.reason });
        });

        gameConnection.channel.on('character_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.state) setCharacterState(prev => ({ ...prev, ...payload.state }));
          setConnection(prev => ({ ...prev, lastActivity: new Date().toISOString() }));
          addRawEvent('character_update', payload);
        });

        gameConnection.channel.on('position_update', (payload) => {
          if (!isMountedRef.current) return;
          setCharacterState(prev => ({ ...prev, position: payload.position }));
          addRawEvent('position_update', payload);
        });

        gameConnection.channel.on('health_update', (payload) => {
          if (!isMountedRef.current) return;
          setCharacterState(prev => ({ ...prev, health: payload.health }));
          addRawEvent('health_update', payload);
        });

        // 🔥 Итерация 7: инвентарь — через Redux
        gameConnection.channel.on('inventory_update', (payload) => {
          if (!isMountedRef.current) return;
          const inv = payload?.inventory;
          if (inv && typeof inv === 'object') {
            dispatch(updateInventory(inv));
          }
          addRawEvent('inventory_update', payload);
        });

        gameConnection.channel.on('environment_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.environment && typeof payload.environment === 'object' && Object.keys(payload.environment).length > 0) {
            setEnvironment(prev => ({ ...(prev || {}), ...payload.environment }));
            dispatch(updateEnvironment(payload.environment));
            addRawEvent('environment_update', payload);
          }
        });

        gameConnection.channel.on('location_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.location && payload.location.id && payload.location.id !== 'unknown') {
            setLocationInfo(prev => ({ ...(prev || {}), ...payload.location }));
            dispatch(updateLocationInfo(payload.location));
            setCacheData('location', characterId, payload.location);
            if (payload.location.environment && typeof payload.location.environment === 'object' && Object.keys(payload.location.environment).length > 0) {
              setEnvironment(prev => ({ ...(prev || {}), ...payload.location.environment }));
              dispatch(updateEnvironment(payload.location.environment));
            }
            addRawEvent('location_update', payload);
          }
        });

        gameConnection.channel.on('emotion_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.emotions && typeof payload.emotions === 'object' && Object.keys(payload.emotions).length > 0) {
            setEmotions(prev => ({ ...(prev || {}), ...payload.emotions }));
            dispatch(updateEmotions(payload.emotions));
            setCacheData('emotions', characterId, payload.emotions);
          }
          addRawEvent('emotion_update', payload);
        });

        gameConnection.channel.on('goal_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.goals && Array.isArray(payload.goals)) {
            setGoals(payload.goals);
            dispatch(updateGoals(payload.goals));
            setCacheData('goals', characterId, payload.goals);
          }
          addRawEvent('goal_update', payload);
        });

        gameConnection.channel.on('task_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.tasks && Array.isArray(payload.tasks)) {
            setTasks(payload.tasks);
            dispatch(updateTasks(payload.tasks));
            setCacheData('tasks', characterId, payload.tasks);
          }
          addRawEvent('task_update', payload);
        });

        gameConnection.channel.on('intent_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.intents && Array.isArray(payload.intents)) {
            setIntents(payload.intents);
            dispatch(updateIntents(payload.intents));
            setCacheData('intents', characterId, payload.intents);
          }
          addRawEvent('intent_update', payload);
        });

        gameConnection.channel.on('dialogue_response', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.response) {
            dispatch(addChatMessage({
              sender: 'ai',
              text: payload.response.text || '...',
              time: new Date().toLocaleTimeString(),
              emotion: payload.response.emotion,
              confidence: payload.response.confidence,
              type: payload.response.type
            }));
          }
          addRawEvent('dialogue_response', payload);
        });

        gameConnection.channel.on('thought_stream_update', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.thoughts && Array.isArray(payload.thoughts)) {
            setConsciousness(prev => ({ ...(prev || {}), recent_reflections: payload.thoughts }));
            dispatch(updateConsciousness({ recent_reflections: payload.thoughts }));
          }
          addRawEvent('thought_stream_update', payload);
        });

        gameConnection.channel.on('suggestion_result', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.result) {
            dispatch(addChatMessage({
              sender: 'system',
              text: `💡 ${payload.result.result || 'Подсказка обработана'}`,
              time: new Date().toLocaleTimeString()
            }));
          }
          addRawEvent('suggestion_result', payload);
        });

        gameConnection.channel.on('system_message', (payload) => {
          if (!isMountedRef.current) return;
          if (payload.text) {
            dispatch(addChatMessage({
              sender: 'system',
              text: payload.text,
              time: new Date().toLocaleTimeString()
            }));
          }
          addRawEvent('system_message', payload);
        });

        gameConnection.channel.on('dialogue_cleared', (payload) => {
          if (!isMountedRef.current) return;
          dispatch(clearChatMessages());
          addRawEvent('dialogue_cleared', payload);
        });

        gameConnection.channel.on('dialog_event', (payload) => {
          if (!isMountedRef.current) return;
          addGameEvent('dialog', payload.dialog || payload);
          addRawEvent('dialog_event', payload);
        });

        gameConnection.channel.on('action_result', (payload) => {
          if (!isMountedRef.current) return;
          setActionInProgress(false);
          addRawEvent('action_result', payload);
          if (payload.result?.success) {
            addGameEvent('system', { message: `Действие "${payload.action}" успешно`, type: 'success' });
          }
        });

        gameConnection.channel.onError((error) => {
          if (!isMountedRef.current) return;
          setConnection(prev => ({ ...prev, status: 'error', error: error?.message || 'Channel error', isInitializing: false }));
        });

        gameConnection.channel.onClose(() => {
          if (!isMountedRef.current) return;
          setConnection(prev => ({ ...prev, status: 'disconnected', isInitializing: false }));
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
        });
      }

      if (isMountedRef.current) {
        setConnection(prev => ({ ...prev, socket: gameConnection.socket, channel: gameConnection.channel, serverUrl: server_url }));
      }

      await gameConnection.join();

      startPing(gameConnection.channel);
      saveSelectedCharacter(characterId, characterStateRef.current);

      return gameConnection;

    } catch (error) {
      if (isMountedRef.current) {
        setConnection(prev => ({ ...prev, status: 'error', error: error.message, isInitializing: false }));
      }
      addGameEvent('system', { message: `Ошибка подключения: ${error.message}`, type: 'error' });
      throw error;
    }
  }, [addGameEvent, addRawEvent, startPing, loadAllData, loadInventory, loadDetailedTasks, setCacheData, dispatch]);

  // sendAction
  const sendAction = useCallback(async (action, params = {}) => {
    const currentConnection = connectionRef.current;
    if (currentConnection.isInitializing || !currentConnection.channel || currentConnection.status !== 'connected') {
      const error = currentConnection.isInitializing ? 'Персонаж еще инициализируется...' : 'Не подключено к игре';
      addGameEvent('error', { message: error, type: 'warning' });
      throw new Error(error);
    }

    setActionInProgress(true);
    try {
      addGameEvent('system', { message: `Выполняется действие: ${action}`, type: 'info' });
      const result = await new Promise((resolve, reject) => {
        const actionTimeout = setTimeout(() => reject(new Error('Action timeout')), ACTION_TIMEOUT);
        currentConnection.channel.push('perform_action', { action, params })
          .receive('ok', (resp) => { clearTimeout(actionTimeout); resolve(resp); })
          .receive('error', (resp) => { clearTimeout(actionTimeout); reject(new Error(resp.reason || 'Action error')); })
          .receive('timeout', () => { clearTimeout(actionTimeout); reject(new Error('Action timeout')); });
      });

      if (currentConnection.characterId) {
        setTimeout(() => {
          loadLocationInfo(currentConnection.characterId, true);
          loadAvailableActions(currentConnection.characterId, true);
          loadEmotions(currentConnection.characterId, true);
          loadGoals(currentConnection.characterId, true);
          loadTasks(currentConnection.characterId, true);
          loadIntents(currentConnection.characterId, true);
          loadConsciousness(currentConnection.characterId, true);
          loadInventory(currentConnection.characterId); // 🔥 инвентарь тоже
          loadDetailedTasks(currentConnection.characterId, true).catch(() => {});
        }, 500);
      }

      return result;
    } catch (error) {
      addGameEvent('system', { message: `Ошибка при выполнении действия: ${error.message}`, type: 'error' });
      throw error;
    } finally {
      setActionInProgress(false);
    }
  }, [addGameEvent, loadLocationInfo, loadAvailableActions, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness, loadInventory, loadDetailedTasks]);

  // updatePosition
  const updatePosition = useCallback((position) => {
    const currentConnection = connectionRef.current;
    if (currentConnection.isInitializing || !currentConnection.channel || currentConnection.status !== 'connected') {
      throw new Error('Не подключено к игре или идет инициализация');
    }
    return new Promise((resolve, reject) => {
      const positionTimeout = setTimeout(() => reject(new Error('Position update timeout')), 10000);
      currentConnection.channel.push('update_position', { position })
        .receive('ok', (resp) => { clearTimeout(positionTimeout); resolve(resp); })
        .receive('error', (resp) => { clearTimeout(positionTimeout); reject(new Error(resp.reason || 'Position update error')); })
        .receive('timeout', () => { clearTimeout(positionTimeout); reject(new Error('Position update timeout')); });
    });
  }, []);

  // refreshState
  const refreshState = useCallback(async () => {
    const currentConnection = connectionRef.current;
    if (currentConnection.isInitializing || !currentConnection.channel || currentConnection.status !== 'connected') {
      throw new Error('Не подключено к игре или идет инициализация');
    }
    try {
      const state = await new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(null), 5000);
        currentConnection.channel.push('get_state', {})
          .receive('ok', (response) => { clearTimeout(timeout); resolve(response); })
          .receive('error', () => { clearTimeout(timeout); resolve(null); })
          .receive('timeout', () => { clearTimeout(timeout); resolve(null); });
      });
      if (state) setCharacterState(prev => ({ ...prev, ...state }));
      return state;
    } catch (error) {
      throw error;
    }
  }, []);

  // loadAvailableCharacters
  const loadAvailableCharacters = useCallback(async (forceRefresh = false) => {
    if (isLoadingCharactersRef.current && !forceRefresh) return;
    if (!isAuthenticated) return;
    try {
      isLoadingCharactersRef.current = true;
      setCharactersLoading(true);
      const response = await Gateway.api.character.getCharactersByPlayer(user?.id);
      const characters = Gateway.utils.characters.extractDataFromResponse(response, 'characters');
      if (isMountedRef.current) {
        setAvailableCharacters(characters);
        hasLoadedCharactersRef.current = true;
      }
    } catch (error) {
      if (isMountedRef.current) setAvailableCharacters([]);
      addGameEvent('system', { message: 'Ошибка загрузки персонажей', type: 'error', details: error.message });
    } finally {
      isLoadingCharactersRef.current = false;
      if (isMountedRef.current) setCharactersLoading(false);
    }
  }, [user?.id, isAuthenticated, addGameEvent]);

  // selectCharacter
  const selectCharacter = useCallback(async (character, autoConnect = true) => {
    if (!character?.id) return false;
    try {
      cleanup(true);
      let fullCharacterData = character;
      if (!character.name || !character.race) {
         const characterResponse = await Gateway.api.character.getCharacter(character.id);
         fullCharacterData = characterResponse.data || characterResponse;
      }
      saveSelectedCharacter(character.id, fullCharacterData);
      if (isMountedRef.current) {
        setSelectedCharacter(fullCharacterData);
        setCharacterState(fullCharacterData);
        setConnection(prev => ({ ...prev, characterId: character.id }));
      }
      if (autoConnect) await connect(character.id);
      addGameEvent('system', { message: `Выбран персонаж "${fullCharacterData.name}"`, type: 'success' });
      return true;
    } catch (error) {
      clearCharacterData();
      addGameEvent('system', { message: `Ошибка выбора персонажа: ${error.message}`, type: 'error' });
      return false;
    }
  }, [cleanup, connect, addGameEvent]);

  // disconnect
  const disconnect = useCallback(() => {
    addGameEvent('system', { message: 'Отключение от игры', type: 'info' });
    cleanup(true);
  }, [cleanup, addGameEvent]);

  // switchCharacter
  const switchCharacter = useCallback(async (newCharacterId) => {
    const newCharacter = availableCharacters.find(c => c.id === newCharacterId);
    if (!newCharacter) throw new Error('Персонаж не найден');
    return await selectCharacter(newCharacter);
  }, [availableCharacters, selectCharacter]);

  // ============================================================================
  // ЭФФЕКТЫ
  // ============================================================================
  useEffect(() => {
    if (isAuthenticated && user?.id && !hasLoadedCharactersRef.current) loadAvailableCharacters();
    if (!isAuthenticated) {
      hasLoadedCharactersRef.current = false;
      isLoadingCharactersRef.current = false;
      if (connectionRef.current.status !== 'disconnected') {
        cleanup();
        clearCharacterData();
      }
    }
  }, [isAuthenticated, user?.id, loadAvailableCharacters, cleanup]);

  useEffect(() => {
    if (isAuthenticated && availableCharacters.length > 0 && connection.status === 'disconnected' && shouldAutoConnect) {
      const savedCharacterId = getSelectedCharacterId();
      if (savedCharacterId) {
        const savedCharacter = availableCharacters.find(c => c.id === savedCharacterId);
        if (savedCharacter && !selectedCharacter) {
          selectCharacter(savedCharacter, true).catch(() => {});
        }
      }
    }
  }, [isAuthenticated, availableCharacters, connection.status, shouldAutoConnect, selectedCharacter, selectCharacter]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    if (connection.status === 'error' && connection.characterId && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setReconnectAttempts(prev => prev + 1);
        connect(connection.characterId, { reconnect: false, maxRetries: 1 }).catch(() => {});
      }, RECONNECT_DELAY * (reconnectAttempts + 1));
    }
    return () => { if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current); };
  }, [connection.status, connection.characterId, reconnectAttempts, connect]);

  useEffect(() => {
    if (connection.status === 'connected') {
      setReconnectAttempts(0);
      setShouldAutoConnect(true);
    }
  }, [connection.status]);

  // ============================================================================
  // ПУБЛИЧНЫЙ API
  // ============================================================================
  return {
    connection,
    characterState,
    events,
    rawEvents,
    actionInProgress,
    reconnectAttempts,
    availableCharacters,
    charactersLoading,
    selectedCharacter,
    selectedCharacterId: connection.characterId,
    
    locationInfo,
    availableActions,
    actionsLoading,
    environment,
    emotions,
    goals,
    tasks,
    intents,
    quests,
    nearbyPlayers,
    consciousness,
    detailedTasks,
    
    connect,
    disconnect,
    sendAction,
    updatePosition,
    refreshState,
    selectCharacter,
    switchCharacter,
    loadAvailableCharacters,
    loadLocationInfo,
    loadAvailableActions,
    loadEmotions,
    loadGoals,
    loadTasks,
    loadIntents,
    loadConsciousness,
    loadDetailedTasks,
    loadAllData,
    loadCharacterData,
    refreshAllData,
    sendStimulus,
    sendDialogue,
    sendSuggestion,
    getThoughtStream,
    addGameEvent,
    addRawEvent,
    clearEvents,
    clearRawEvents,

    // 🔥 Итерация 7: инвентарь
    loadInventory,
    
    isConnected: connection.status === 'connected' && !connection.isInitializing,
    isConnecting: connection.status === 'connecting',
    isInitializing: connection.isInitializing,
    isError: connection.status === 'error',
    isDisconnected: connection.status === 'disconnected',
    hasCharacters: availableCharacters.length > 0,
    autoConnectPending: false,
    
    cleanup,
    
    utils: {
      getSelectedCharacterId,
      saveSelectedCharacter,
      clearCharacterData,
      hasAuthToken,
    }
  };
};

export default useGameClient;
